import type { BadgeTone } from '@wundo/ui';
import type { SkillRecommendationStatus } from '@wundo/shared/userSkillOverview.ts';

export function formatSkillDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatSkillDateTime(iso: string | null, timezone?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

/**
 * How far off a moment is, in the coarsest unit that still says something.
 *
 * Schedules are read to answer "has this fired yet, and when is the next one" —
 * a timestamp answers neither at a glance.
 */
export function relativeFromNow(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const future = ms > 0;

  const minutes = Math.round(abs / 60_000);
  if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return future ? `in ${days}d` : `${days}d ago`;

  const months = Math.round(days / 30);
  return future ? `in ${months}mo` : `${months}mo ago`;
}

export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

export function recommendationStatusTone(status: SkillRecommendationStatus): BadgeTone {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'delivered':
      return 'success';
    case 'skipped':
      return 'neutral';
    case 'superseded':
      return 'info';
    case 'expired':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Above 75 is a strong recommendation, below 50 barely one. */
export function scoreTone(score: number): BadgeTone {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'neutral';
}
